import { useState, useMemo, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MovieCard from '@/components/feature/MovieCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SeoHead from '@/components/base/SeoHead';
import { tvShows, categories } from '@/mocks/tvShows';

const sortOptions = [
  { label: '默认排序', value: 'default' },
  { label: '评分最高', value: 'rating' },
  { label: '最近更新', value: 'latest' },
  { label: '最受欢迎', value: 'popular' },
];

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState('default');
  const [activeTag, setActiveTag] = useState('all');

  const currentCategory = categories.find(c => c.slug === slug) || { name: '全部', slug: 'all' };

  const filteredShows = useMemo(() => {
    let result = slug && slug !== 'all'
      ? tvShows.filter(s => s.categorySlug === slug)
      : [...tvShows];

    if (activeTag !== 'all') {
      result = result.filter(s => s.tags.includes(activeTag));
    }

    switch (sortBy) {
      case 'rating':
        result.sort((a, b) => b.rating - a.rating);
        break;
      case 'latest':
        result.sort((a, b) => b.year - a.year);
        break;
      case 'popular':
        result.sort((a, b) => parseFloat(b.totalViews) - parseFloat(a.totalViews));
        break;
      default:
        break;
    }
    return result;
  }, [slug, activeTag, sortBy]);

  const allTags = useMemo(() => {
    const tagSet = new Set<string>();
    (slug && slug !== 'all'
      ? tvShows.filter(s => s.categorySlug === slug)
      : tvShows
    ).forEach(s => s.tags.forEach(t => tagSet.add(t)));
    return ['all', ...Array.from(tagSet)];
  }, [slug]);

  useEffect(() => {
    setActiveTag('all');
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug, sortBy]);

  const categoryName = currentCategory.name;
  const seoTitle = `${categoryName} - 97影院电视剧全集在线观看，热门${categoryName}免费高清不卡顿`;
  const seoDesc = `97影院提供热门${categoryName}全集在线观看，最新${categoryName}更新快，高清蓝光不卡顿流畅播放。海量${categoryName}资源尽在97影院。`;
  const seoKeywords = `${categoryName},${categoryName}全集,${categoryName}在线观看,热门${categoryName},高清${categoryName},免费${categoryName},97影院`;

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryName} - 97影院`,
    description: seoDesc,
    url: `https://97yingyuan.com/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '97影院',
      url: 'https://97yingyuan.com',
    },
  };

  return (
    <>
      <SeoHead
        title={seoTitle}
        description={seoDesc}
        keywords={seoKeywords}
        canonicalUrl={`https://97yingyuan.com/category/${slug}`}
        ogImage={filteredShows[0]?.poster}
        schemaData={schemaData}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="w-full px-4 md:px-6 py-6">
            <div className="max-w-7xl mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: categoryName },
                ]}
              />

              <div className="mt-4 mb-6">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                  {categoryName}
                </h1>
                <p className="text-foreground-400 text-sm mt-2">
                  共找到 <strong className="text-primary-500">{filteredShows.length}</strong> 部{categoryName}
                </p>
              </div>

              <div className="flex flex-col lg:flex-row gap-6">
                <aside className="lg:w-48 flex-shrink-0">
                  <div className="lg:sticky lg:top-20">
                    <h3 className="text-sm font-semibold text-foreground-700 mb-3">分类筛选</h3>
                    <div className="flex lg:flex-col gap-1.5 flex-wrap">
                      {categories.map((cat) => (
                        <Link
                          key={cat.slug}
                          to={`/category/${cat.slug}`}
                          className={`px-3 py-2 text-sm rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                            slug === cat.slug || (!slug && cat.slug === 'all')
                              ? 'bg-primary-500/20 text-primary-500 font-medium'
                              : 'text-foreground-500 hover:text-foreground-800 hover:bg-background-100'
                          }`}
                        >
                          {cat.name}
                        </Link>
                      ))}
                    </div>

                    {allTags.length > 1 && (
                      <>
                        <h3 className="text-sm font-semibold text-foreground-700 mt-6 mb-3">标签筛选</h3>
                        <div className="flex lg:flex-col gap-1.5 flex-wrap">
                          {allTags.map((tag) => (
                            <button
                              key={tag}
                              onClick={() => setActiveTag(tag)}
                              className={`px-3 py-1.5 text-sm rounded-lg text-left transition-colors cursor-pointer whitespace-nowrap ${
                                activeTag === tag
                                  ? 'bg-accent-500/20 text-accent-500 font-medium'
                                  : 'text-foreground-500 hover:text-foreground-800 hover:bg-background-100'
                              }`}
                            >
                              {tag === 'all' ? '全部' : tag}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </aside>

                <div className="flex-1">
                  <div className="flex items-center justify-between mb-5">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-foreground-400">排序：</span>
                      <div className="flex items-center gap-1">
                        {sortOptions.map((opt) => (
                          <button
                            key={opt.value}
                            onClick={() => setSortBy(opt.value)}
                            className={`px-3 py-1.5 text-xs rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                              sortBy === opt.value
                                ? 'bg-secondary-500 text-background-50 font-medium'
                                : 'text-foreground-500 bg-background-100 hover:bg-background-200'
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <span className="text-xs text-foreground-400">
                      共 {filteredShows.length} 部
                    </span>
                  </div>

                  {filteredShows.length > 0 ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
                      {filteredShows.map((show) => (
                        <MovieCard key={show.id} show={show} />
                      ))}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-20 text-center">
                      <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-background-100 mb-4">
                        <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
                      </div>
                      <p className="text-foreground-400 text-sm">暂无相关内容</p>
                      <Link
                        to="/"
                        className="mt-4 text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        返回首页
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}